import { type ReactElement, cloneElement, useEffect, useState } from "react";
import {
  Button,
  Chip,
  Description,
  Label,
  Modal,
  NumberField,
  Spinner,
  toast,
} from "@heroui/react";
import { NumberValue, RadioButtonGroup } from "@heroui-pro/react";
import {
  StorageAPI,
  StorageQuotaPeriod,
  type StorageQuotaPackage,
} from "@repo/api";
import dayjs from "dayjs";
import BridgeHelper from "@/utils/rn-bridge/bridge-helper";
import { NavCode } from "@/utils/rn-bridge/nav-code";

const MIN_GB = 10;
const MAX_GB = 200;
const STEP_GB = 10;

const PERIOD_OPTIONS = [
  { value: StorageQuotaPeriod.Month, label: "月付", description: "30天" },
  { value: StorageQuotaPeriod.Year, label: "年付", description: "365天", badge: "75折" },
] as const;

const WEBVIEW_FOCUS_EVENT = "furries-webview-focus";

type PurchaseStorageModalProps = {
  trigger: ReactElement<{ onPress?: () => void }>;
  /** 支付流程结束后刷新配额（成功或从收银台返回） */
  onPaid?: () => void;
};

export default function PurchaseStorageModal({ trigger, onPaid }: PurchaseStorageModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [capacityGB, setCapacityGB] = useState(MIN_GB);
  const [period, setPeriod] = useState<StorageQuotaPeriod>(StorageQuotaPeriod.Month);
  const [packages, setPackages] = useState<StorageQuotaPackage[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  /** 已跳转收银台，等待从原生页返回 */
  const [awaitingCheckout, setAwaitingCheckout] = useState(false);

  const quote = packages?.find((pkg) => pkg.size_gb === capacityGB && pkg.period === period);
  const expireDate = quote ? dayjs().add(quote.days, "day").format("YYYY-MM-DD") : null;

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setIsError(false);
    StorageAPI.listQuotaPackages()
      .then(({ data }) => {
        if (!cancelled) {
          setPackages(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIsError(true);
          setPackages(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // 从 App 收银台返回时：刷新配额（弹窗在跳转时已关闭）
  useEffect(() => {
    const onFocus = () => {
      if (!awaitingCheckout) {
        return;
      }
      setAwaitingCheckout(false);
      setCapacityGB(MIN_GB);
      setPeriod(StorageQuotaPeriod.Month);
      onPaid?.();
    };
    window.addEventListener(WEBVIEW_FOCUS_EVENT, onFocus);
    return () => window.removeEventListener(WEBVIEW_FOCUS_EVENT, onFocus);
  }, [awaitingCheckout, onPaid]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open && !awaitingCheckout) {
      setCapacityGB(MIN_GB);
      setPeriod(StorageQuotaPeriod.Month);
    }
  };

  const handlePurchase = async () => {
    if (!quote || isPurchasing) {
      return;
    }
    setIsPurchasing(true);
    try {
      const { data: order } = await StorageAPI.buyQuota({
        size_gb: quote.size_gb,
        period: quote.period,
      });
      setAwaitingCheckout(true);
      setIsOpen(false);
      BridgeHelper.sendMessage({
        nav_code: NavCode.NAV_PAYMENT_COUNTER,
        data: {
          order_id: order.order_id,
          notes: quote.title || `容量扩容 ${quote.size_gb}GB`,
          price: order.amount,
        },
      });
    } catch (error) {
      const msg =
        (error as { msg?: string }).msg ?? (error as { message?: string }).message ?? "";
      toast.danger(msg || "下单失败，请稍后再试");
    } finally {
      setIsPurchasing(false);
    }
  };

  return (
    <>
      {cloneElement(trigger, {
        onPress: () => setIsOpen(true),
      })}
      <Modal.Backdrop isOpen={isOpen} onOpenChange={handleOpenChange}>
        <Modal.Container>
          <Modal.Dialog className="max-w-md">
            {({ close }) => (
              <>
                <Modal.Body className="flex flex-col gap-5">
                  <div className="flex flex-col items-center gap-2">
                    <div className="text-foreground text-2xl font-semibold">购买更多存储容量</div>
                    <div className="text-muted text-sm">按需选择容量与时长，年付更划算</div>
                  </div>

                  <NumberField
                    aria-label="购买容量"
                    className="items-center gap-2"
                    maxValue={MAX_GB}
                    minValue={MIN_GB}
                    step={STEP_GB}
                    value={capacityGB}
                    variant="secondary"
                    onChange={(value) => {
                      if (typeof value === "number" && !Number.isNaN(value)) {
                        setCapacityGB(value);
                      }
                    }}
                  >
                    <Label>容量（GB）</Label>
                    <NumberField.Group>
                      <NumberField.DecrementButton />
                      <NumberField.Input className="w-24 text-center" />
                      <NumberField.IncrementButton />
                    </NumberField.Group>
                  </NumberField>

                  <div className="flex flex-col gap-2">
                    <Label>计费周期</Label>
                    <RadioButtonGroup
                      aria-label="付费时长"
                      className="grid-cols-2"
                      layout="grid"
                      name="quota-period"
                      value={period}
                      variant="secondary"
                      onChange={(value) => setPeriod(value as StorageQuotaPeriod)}
                    >
                      {PERIOD_OPTIONS.map((option) => (
                        <RadioButtonGroup.Item key={option.value} value={option.value}>
                          <RadioButtonGroup.Indicator />
                          <RadioButtonGroup.ItemContent>
                            <Label className="flex items-center gap-1.5">
                              {option.label}
                              {"badge" in option && (
                                <Chip color="success" className="bg-green-100" size="sm">
                                  <Chip.Label>{option.badge}</Chip.Label>
                                </Chip>
                              )}
                            </Label>
                            <Description>{option.description}</Description>
                          </RadioButtonGroup.ItemContent>
                        </RadioButtonGroup.Item>
                      ))}
                    </RadioButtonGroup>
                  </div>

                  <div className="flex flex-col gap-2">
                    <div className="text-muted text-center">购买后有效期至：{expireDate}</div>
                    <div className="text-muted text-center">
                      多个扩容包可叠加，每个扩容包的到期日期独立计算
                    </div>
                  </div>
                </Modal.Body>
                <Modal.Footer>
                  <div className="text-foreground flex min-h-12 flex-1 flex-col items-start justify-center gap-1.5 text-sm">
                    {isLoading && (
                      <div className="text-muted flex items-center gap-2">
                        <Spinner size="sm" />
                        询价中…
                      </div>
                    )}
                    {isError && <div className="text-danger">价格获取失败，请关闭后重试</div>}
                    {!isLoading && !isError && quote && (
                      <div className="text-lg">
                        合计
                        <NumberValue
                          className="text-accent ml-1 text-xl font-semibold"
                          currency="CNY"
                          style="currency"
                          value={quote.amount / 100}
                        />
                      </div>
                    )}
                    {!isLoading && !isError && packages && !quote && (
                      <div className="text-danger">当前容量档位暂不可购买</div>
                    )}
                  </div>
                  <Button
                    className="rounded-full bg-gray-100 text-[#333333]"
                    isDisabled={isPurchasing}
                    onPress={close}
                  >
                    取消
                  </Button>
                  <Button
                    className="bg-primary rounded-full text-white"
                    isDisabled={!quote || isPurchasing}
                    onPress={() => void handlePurchase()}
                  >
                    {isPurchasing ? "下单中…" : "立即支付"}
                  </Button>
                </Modal.Footer>
              </>
            )}
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </>
  );
}
